import { useState, useEffect, useCallback, useRef } from 'react'
import { Hub } from 'aws-amplify/utils'
import { authService } from '../services/auth'
import { merchantService } from '../services/api'
import type { AuthUser } from '../types'

interface AuthState {
  user: AuthUser | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  isMerchant: boolean
  needsProfileCompletion: boolean
  cognitoUser: any
}

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isAuthenticated: false,
    isMerchant: false,
    needsProfileCompletion: false,
    cognitoUser: null
  })

  const loadingRef = useRef(false)
  const isMountedRef = useRef(true)
  const initialLoadRef = useRef(true)

  const updateState = useCallback((updates: Partial<AuthState>) => {
    if (isMountedRef.current) {
      setState(prev => ({ ...prev, ...updates }))
    }
  }, [])

  const loadUser = useCallback(async () => {
    if (loadingRef.current) return
    
    try {
      loadingRef.current = true
      if (!initialLoadRef.current) {
        updateState({ loading: true, error: null })
      }
      
      const currentUser = await authService.getCurrentUser()
      
      if (!currentUser) {
        updateState({ 
          user: null,
          cognitoUser: null,
          loading: false, 
          isAuthenticated: false, 
          isMerchant: false,
          needsProfileCompletion: false
        })
        return
      }

      const userEmail = currentUser.signInDetails?.loginId || currentUser.username

      try {
        const merchant = await merchantService.findByEmail(userEmail)
        
        if (!merchant) {
          updateState({
            user: null,
            cognitoUser: currentUser,
            loading: false,
            isAuthenticated: true,
            isMerchant: false,
            needsProfileCompletion: true
          })
          return
        }
        
        updateState({
          user: {
            id: merchant.id,
            email: merchant.email,
            username: merchant.fullName,
            fullName: merchant.fullName,
            merchantId: merchant.id,
            isMerchant: true,
            isAdmin: false
          },
          cognitoUser: currentUser,
          loading: false,
          isAuthenticated: true,
          isMerchant: true,
          needsProfileCompletion: false
        })
      } catch (error) {
        console.error('Merchant fetch failed:', error)
        updateState({ 
          user: null,
          cognitoUser: currentUser,
          loading: false, 
          error: 'Failed to load user data',
          isAuthenticated: true,
          isMerchant: false,
          needsProfileCompletion: true
        })
      }
    } catch (error) {
      console.error('Load user failed:', error)
      updateState({ 
        user: null,
        cognitoUser: null,
        loading: false, 
        isAuthenticated: false,
        isMerchant: false,
        needsProfileCompletion: false
      })
    } finally {
      loadingRef.current = false
      initialLoadRef.current = false
    }
  }, [updateState])

  useEffect(() => {
    isMountedRef.current = true
    
    const initAuth = async () => {
      await loadUser()
    }

    initAuth()

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          updateState({ loading: true })
          break
        case 'signInWithRedirect_failure':
          console.error('OAuth error:', payload.data)
          updateState({ 
            loading: false, 
            error: 'Social sign in failed. Please try again.' 
          })
          break
        case 'signedIn':
          loadUser()
          break
        case 'signedOut':
          updateState({ 
            user: null,
            cognitoUser: null,
            loading: false, 
            isAuthenticated: false, 
            isMerchant: false,
            needsProfileCompletion: false
          })
          break
        case 'tokenRefresh_failure':
          updateState({
            user: null,
            cognitoUser: null,
            loading: false,
            isAuthenticated: false,
            isMerchant: false,
            needsProfileCompletion: false,
            error: 'Session expired'
          })
          break
      }
    })

    return () => {
      isMountedRef.current = false
      unsubscribe()
    }
  }, [loadUser, updateState])

  const signIn = async (email: string, password: string) => {
    try {
      updateState({ loading: true, error: null })
      const result = await authService.signIn(email, password)
      
      if (result.isSignedIn) {
        await loadUser()
        return { success: true }
      } else {
        updateState({ loading: false, error: 'Sign in incomplete' })
        return { success: false, error: 'Sign in requires additional steps', nextStep: result.nextStep }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Sign in failed'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage, errorName: err.name }
    }
  }

  const signInWithRedirect = async (provider: 'Google' | 'Facebook') => {
    try {
      updateState({ loading: true, error: null })
      await authService.signInWithRedirect(provider)
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Social sign in failed'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const signUp = async (data: any) => {
    try {
      updateState({ loading: true, error: null })
      await authService.signUp(data.email, data.password, {})
      updateState({ loading: false })
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Sign up failed'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const confirmSignUp = async (email: string, code: string) => {
    try {
      updateState({ loading: true, error: null })
      const result = await authService.confirmSignUp(email, code)
      updateState({ loading: false })
      return { success: true, result }
    } catch (err: any) {
      const errorMessage = err.message || 'Verification failed'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const resendSignUpCode = async (email: string) => {
    try {
      updateState({ loading: true, error: null })
      const result = await authService.resendSignUpCode(email)
      updateState({ loading: false })
      return { success: true, result }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to resend code'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      updateState({ loading: true, error: null })
      const result = await authService.resetPassword(email)
      updateState({ loading: false })
      return { success: true, result }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send reset code'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      updateState({ loading: true, error: null })
      await authService.confirmResetPassword(email, code, newPassword)
      updateState({ loading: false })
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to reset password'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const completeProfile = async (profileData: { fullName: string; phoneNumber?: string }) => {
    try {
      if (!state.cognitoUser) {
        throw new Error('No authenticated user found')
      }

      const userEmail = state.cognitoUser.signInDetails?.loginId || state.cognitoUser.username

      updateState({ loading: true, error: null })

      const merchant = await merchantService.create({
        email: userEmail,
        fullName: profileData.fullName,
        phoneNumber: profileData.phoneNumber || '',
        creditBalance: 0,
        totalSpent: 0,
        accountStatus: 'ACTIVE'
      })

      updateState({
        user: {
          id: merchant.id,
          email: merchant.email,
          username: merchant.fullName,
          fullName: merchant.fullName,
          merchantId: merchant.id,
          isMerchant: true,
          isAdmin: false
        },
        loading: false,
        isAuthenticated: true,
        isMerchant: true,
        needsProfileCompletion: false
      })

      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to complete profile'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const signOut = async () => {
    try {
      await authService.signOut()
      updateState({ 
        user: null,
        cognitoUser: null,
        loading: false, 
        isAuthenticated: false, 
        isMerchant: false,
        needsProfileCompletion: false
      })
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Sign out failed'
      return { success: false, error: errorMessage }
    }
  }

  return {
    ...state,
    signIn,
    signInWithRedirect,
    signUp,
    confirmSignUp,
    resendSignUpCode,
    resetPassword,
    confirmResetPassword,
    signOut,
    completeProfile,
    refreshUser: loadUser
  }
}